import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Pencil, Trash2, Image as ImageIcon, CheckSquare, UploadCloud, Check,
  Star, ThumbsUp, Home, Loader2, Download, X, FolderOpen, Ban,
} from "lucide-react";
import Button from "../../components/common/Button";
import VideoPlayer from "../../components/common/VideoPlayer";
import CreateAlbumModal from "../../components/common/CreateAlbumModal";
import UploadToAlbumModal from "../../components/common/UploadToAlbumModal";
import ConfirmModal from "../../components/common/ConfirmModal";
import EditNameModal from "../../components/common/EditNameModal";
import ARoomAlbumPolicyModal from "../../components/common/ARoomAlbumPolicyModal";
import ARoomAlbumViewerApprovalsModal from "../../components/common/ARoomAlbumViewerApprovalsModal";
import {
  getAlbumMedia,
  getGalleriesByEvent,
  archiveMedia,
  invalidateAlbumCache,
  updateEventGallery,
  updateGalleryAlbum,
  archiveEventGallery,
  archiveGalleryAlbum,
  updateAlbumOrder,
  setAlbumThumbnail,
  setHomeImage,
  moderateSubPromoterMedia,
  getARoomAlbumViewerRequests,
} from "../../services/galleryService";
import { downloadMediaFile } from "../../services/uploadService";
import { getRoomAccess } from "../../services/roomService";
import { useUpload } from "../../context/UploadContext";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { resolveEffectiveOwnerId } from "../../utils/eventPermissions";
import Modal from "../../components/common/Modal";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const MAX_MAIN_SCREEN_ALBUMS = 4;

// Share Gallery is a special lifecycle type: the Promoter can only Download
// user-uploaded media in it (no Delete/Star/Home/Upload), can't create new
// albums, and can't rename/delete the gallery itself (see MediaTab.jsx /
// RoomGalleries.jsx, where activate/deactivate is offered instead).
const isShareGallery = (gallery) => gallery?.GalleryType === 3 || gallery?.GalleryType === "ShareGallery";

// The User Gallery ("Social Album" in @Room terms) is the shared bucket the
// backend auto-creates for participant/Event Host uploads — it doesn't
// belong to the Promoter, so the same Download-only restriction as Share
// Gallery applies here: the Promoter can only Download/view what's inside,
// never Edit/Delete/Star/Home/Main-Screen/Upload/create-albums.
const isRestrictedGallery = (gallery) => isShareGallery(gallery) || gallery?.IsUserGallery === true;

const getMainScreenAlbumIds = (albums) =>
  [...albums]
    .filter((album) => album.IsMainScreen)
    .sort((left, right) => {
      const leftOrder = left.MainScreenOrder ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = right.MainScreenOrder ?? Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return left.Id - right.Id;
    })
    .map((album) => album.Id);

const MainScreenAlbumIcon = ({ active }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="3"
      y="4"
      width="18"
      height="12"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M8 20h8M10 16.5h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M7.5 12.5l2.2-2.4 2.1 2.1 3.2-3.1 1.5 1.4"
      stroke={active ? "currentColor" : "currentColor"}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SortableAlbumCard = ({
  album, targetAlbumId, collapsedAlbums, albumMedia, loadingMedia,
  visibleCount, selectedIds, deletingIds, savingAlbumId, deletingAlbumId,
  downloadingIds, mainScreenAlbumIds, togglingMainScreenId,
  selectedCount, INITIAL_SHOW,
  albumThumbnails, togglingThumbnail,
  homeImages, togglingHomeImage,
  onToggle, onEdit, onDelete, onUpload, onSelectAll,
  onToggleSelect, onOpenLightbox, onDownload, onDeleteMedia, onShowMore,
  onToggleThumbnail, onToggleHomeImage, onToggleMainScreen,
  pendingRequestCount = 0, onManageViewerApprovals, isARoom = false,
  restrictedToDownload = false,
}) => {
  const { t } = useTranslation(["gallery", "common"]);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: album.Id });

  const isMainScreen = mainScreenAlbumIds.includes(album.Id);
  const mainScreenOrder = isMainScreen ? mainScreenAlbumIds.indexOf(album.Id) + 1 : null;
  const albumItems = albumMedia[album.Id] || [];
  const allSelectedInAlbum = albumItems.length > 0 && albumItems.every((m) => selectedIds[m.Id]);

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      id={`album-${album.Id}`}
      style={{
        ...styles.albumCard,
        ...(Number(album.Id) === Number(targetAlbumId) ? styles.targetAlbumCard : {}),
        ...dragStyle,
      }}
    >
      {/* Album Header */}
      <div style={styles.albumHeader} onClick={() => onToggle(album.Id)}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {/* Drag handle */}
          <span
            {...attributes}
            {...listeners}
            style={styles.dragHandle}
            onClick={(e) => e.stopPropagation()}
            title={t("galleryDetail.album.dragToReorder")}
          >
            ⠿
          </span>
        </div>
        <div style={styles.albumLeft}>
          <div style={styles.albumNameRow}>
            <span style={styles.albumName}>{album.Name}</span>
            {!restrictedToDownload && (
              <button
                style={{
                  ...styles.mainScreenBtn,
                  ...(isMainScreen ? styles.mainScreenBtnActive : {}),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleMainScreen(album);
                }}
                disabled={togglingMainScreenId === album.Id || savingAlbumId === album.Id || deletingAlbumId === album.Id}
                title={isMainScreen ? t("galleryDetail.album.removeFromMainScreen", { slot: mainScreenOrder }) : t("galleryDetail.album.addToMainScreen")}
              >
                <MainScreenAlbumIcon active={isMainScreen} />
                <span>{togglingMainScreenId === album.Id ? t("galleryDetail.album.saving") : isMainScreen ? t("galleryDetail.album.mainScreenShort", { slot: mainScreenOrder }) : t("galleryDetail.album.mainLabel")}</span>
              </button>
            )}
            {isARoom && (album.ContainsMinors || Number(album.ARoomVisibility) === 3) && (
              <button
                style={{ ...styles.albumActionBtn, position: "relative" }}
                onClick={(e) => { e.stopPropagation(); onManageViewerApprovals(album); }}
                disabled={savingAlbumId === album.Id || deletingAlbumId === album.Id}
                title={t("common:aRoomAlbumPolicy.individualViewerApprovals")}
              >
                <CheckSquare size={12} strokeWidth={2.25} />
                {pendingRequestCount > 0 && <span style={styles.pendingApprovalDot} />}
              </button>
            )}
            {!restrictedToDownload && (
              <button
                style={styles.albumActionBtn}
                onClick={(e) => { e.stopPropagation(); onEdit(album); }}
                disabled={savingAlbumId === album.Id || deletingAlbumId === album.Id}
              >
                {savingAlbumId === album.Id ? t("galleryDetail.album.saving") : <Pencil size={12} strokeWidth={2.25} />}
              </button>
            )}
            {!restrictedToDownload && (
              <button
                style={{ ...styles.albumActionBtn, ...styles.albumDeleteBtn }}
                onClick={(e) => { e.stopPropagation(); onDelete(album); }}
                disabled={savingAlbumId === album.Id || deletingAlbumId === album.Id}
              >
                {deletingAlbumId === album.Id ? t("galleryDetail.album.deleting") : <Trash2 size={12} strokeWidth={2.25} />}
              </button>
            )}
          </div>
          <div style={styles.albumMeta}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><ImageIcon size={12} strokeWidth={2} /> {t("galleryDetail.album.imagesCount", { count: albumMedia[album.Id] != null ? albumMedia[album.Id].length : album.ImageCount })}</span>
            {album.IsPremium && <span style={styles.premiumBadge}><Star size={9} strokeWidth={2.5} /> {t("galleryDetail.album.premium")}</span>}
            {isMainScreen && <span style={styles.mainScreenTag}>{t("galleryDetail.album.mainScreenTag", { slot: mainScreenOrder })}</span>}
            {album.IsWatermark && <span style={styles.tag}>{t("galleryDetail.album.watermark")}</span>}
            {album.ExpiresAt && (
              <span style={styles.tag}>{t("galleryDetail.album.expires", { date: new Date(album.ExpiresAt).toLocaleDateString() })}</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {(albumMedia[album.Id] || []).length > 0 && (
            <button
              style={styles.selectAllBtn}
              onClick={(e) => { e.stopPropagation(); onSelectAll(album.Id); }}
              title={allSelectedInAlbum ? t("galleryDetail.album.deselectAllInAlbum") : t("galleryDetail.album.selectAllInAlbum")}
            >
              <CheckSquare size={12} strokeWidth={2.25} /> {allSelectedInAlbum ? t("galleryDetail.album.deselectAll") : t("galleryDetail.album.selectAll")}
            </button>
          )}
          {!restrictedToDownload && (
            <button
              style={styles.uploadBtn}
              onClick={(e) => { e.stopPropagation(); onUpload(album); }}
              title={t("galleryDetail.album.uploadToAlbum")}
            >
              <UploadCloud size={13} strokeWidth={2.25} /> {t("galleryDetail.album.upload")}
            </button>
          )}
          <span style={styles.chevron}>{collapsedAlbums[album.Id] ? "▼" : "▲"}</span>
        </div>
      </div>

      {/* Album Media Grid */}
      {!collapsedAlbums[album.Id] && (
        <div style={styles.imagesGrid}>
          {loadingMedia[album.Id] ? (
            <p style={{ color: "#888", fontSize: 13, padding: "16px 0 8px" }}>{t("galleryDetail.album.loadingMedia")}</p>
          ) : !albumMedia[album.Id] || albumMedia[album.Id].length === 0 ? (
            <p style={{ color: "#888", fontSize: 13, padding: "16px 0 8px" }}>{t("galleryDetail.album.noMedia")}</p>
          ) : (
            <>
              {albumMedia[album.Id]
                .slice(0, visibleCount[album.Id] || INITIAL_SHOW)
                .map((item, idx) => (
                  <div
                    key={item.Id}
                    style={{
                      ...styles.mediaCard,
                      outline: selectedIds[item.Id] ? "3px solid #6c63ff" : "none",
                      outlineOffset: -3,
                    }}
                    onClick={() => selectedCount > 0 ? onToggleSelect(item.Id, album.Id) : onOpenLightbox(album.Id, idx)}
                  >
                    <button
                      style={{
                        ...styles.selectBtn,
                        background: selectedIds[item.Id] ? "#6c63ff" : "rgba(0,0,0,0.45)",
                      }}
                      onClick={(e) => { e.stopPropagation(); onToggleSelect(item.Id, album.Id); }}
                    >
                      {selectedIds[item.Id] ? <Check size={13} strokeWidth={3} /> : ""}
                    </button>
                    {!restrictedToDownload && (
                      <button
                        style={{ ...styles.deleteBtn, opacity: deletingIds[item.Id] ? 0.5 : 1 }}
                        title={t("galleryDetail.album.delete")}
                        onClick={(e) => { e.stopPropagation(); onDeleteMedia(album.Id, item.Id); }}
                        disabled={deletingIds[item.Id]}
                      >
                        <Trash2 size={13} strokeWidth={2.25} />
                      </button>
                    )}
                    <button
                      style={{
                        ...styles.downloadBtn,
                        opacity: downloadingIds[item.Id] ? 0.6 : 1,
                        cursor: downloadingIds[item.Id] ? "not-allowed" : "pointer",
                      }}
                      title={t("galleryDetail.album.download")}
                      onClick={(e) => { e.stopPropagation(); onDownload(item); }}
                      disabled={!!downloadingIds[item.Id]}
                    >
                      {downloadingIds[item.Id] ? <Loader2 size={13} strokeWidth={2.25} style={{ animation: "spin 0.8s linear infinite" }} /> : <Download size={13} strokeWidth={2.25} />}
                    </button>
                    {!restrictedToDownload && (
                      <button
                        style={{
                          ...styles.thumbBtn,
                          background: (albumThumbnails[album.Id] || []).includes(item.Id)
                            ? "rgba(251,191,36,0.9)"
                            : "rgba(0,0,0,0.45)",
                          opacity: togglingThumbnail[item.Id] ? 0.5 : 1,
                        }}
                        title={(albumThumbnails[album.Id] || []).includes(item.Id) ? t("galleryDetail.album.removeThumbnail") : t("galleryDetail.album.setAsThumbnail")}
                        onClick={(e) => { e.stopPropagation(); onToggleThumbnail(album.Id, item.Id); }}
                        disabled={togglingThumbnail[item.Id]}
                      >
                        <Star size={13} strokeWidth={2.25} />
                      </button>
                    )}
                    {!restrictedToDownload && (
                      <button
                        style={{
                          ...styles.homeBtn,
                          background: (homeImages[album.Id] || []).includes(item.Id)
                            ? "rgba(34,197,94,0.92)"
                            : "rgba(0,0,0,0.45)",
                          opacity: togglingHomeImage[item.Id] ? 0.5 : 1,
                        }}
                        title={(homeImages[album.Id] || []).includes(item.Id) ? t("galleryDetail.album.removeHomeImage") : t("galleryDetail.album.setAsHomeImage")}
                        onClick={(e) => { e.stopPropagation(); onToggleHomeImage(album.Id, item.Id); }}
                        disabled={togglingHomeImage[item.Id]}
                      >
                        <Home size={13} strokeWidth={2.25} />
                      </button>
                    )}
                    {item.VideoURL ? (
                      <>
                        <img
                          src={item.VideoThumbnail || item.MaxCompressedUrl || item.CompressedUrl || item.Url}
                          alt={item.Name || t("galleryDetail.album.videoAlt", { id: item.Id })}
                          style={styles.image}
                          loading="lazy"
                        />
                        <div style={styles.playOverlay}>
                          <div style={styles.playBtn}>▶</div>
                        </div>
                      </>
                    ) : (
                      <img
                        src={item.MaxCompressedUrl || item.CompressedUrl || item.Url}
                        alt={item.Name || t("galleryDetail.album.imageAlt", { id: item.Id })}
                        style={styles.image}
                        loading="lazy"
                        onError={(e) => { e.target.src = item.Url; }}
                      />
                    )}
                    {item.LikesCount > 0 && (
                      <div style={{ ...styles.likesOverlay, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <ThumbsUp size={10} strokeWidth={2.5} /> {item.LikesCount}
                      </div>
                    )}
                    {item.IsAlbumThumbnail && (
                      <div style={styles.albumThumbBadge} title={t("galleryDetail.album.albumThumbnailBadge")}>
                        <ImageIcon size={11} strokeWidth={2.25} />
                      </div>
                    )}
                    {item.IsHomeImage && (
                      <div style={styles.homeImageBadge} title={t("galleryDetail.album.homeImageBadge")}>
                        <Home size={11} strokeWidth={2.25} />
                      </div>
                    )}
                  </div>
                ))}
              {albumMedia[album.Id].length > (visibleCount[album.Id] || INITIAL_SHOW) && (
                <button style={styles.showMoreBtn} onClick={() => onShowMore(album.Id)}>
                  {t("galleryDetail.album.showMore", { count: albumMedia[album.Id].length - (visibleCount[album.Id] || INITIAL_SHOW) })}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const GalleryDetail = () => {
  const { t } = useTranslation(["gallery", "common"]);
  const { eventId, roomId, galleryId } = useParams();
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const targetAlbumId = Number(new URLSearchParams(search).get("albumId")) || null;
  const didFocusTargetAlbumRef = useRef(false);
  const initialGallery = state?.gallery;
  const { user, subAccountContext } = useAuth();
  const effectiveOwnerId = resolveEffectiveOwnerId(user, subAccountContext);
  const canModerateSingleMedia = !subAccountContext?.IsSubAccount || Boolean(subAccountContext?.Permissions?.CanManageSingleMedia);

  // backPath works for both event and room flows
  const backPath =
    state?.backPath ||
    (roomId ? `/rooms/${roomId}/galleries` : `/events/${eventId}`);
  // RoomGalleries.jsx only renders its @Room header (name, stats, Share/Chat/
  // Settings) when it receives `room` via navigation state — without
  // forwarding it back here, "Back to Galleries" landed on a bare gallery
  // grid with no room context at all.
  const backState = { room: state?.room };

  const [gallery, setGallery] = useState(initialGallery);
  const [isResolvingGallery, setIsResolvingGallery] = useState(!initialGallery);
  const [albums, setAlbums] = useState(initialGallery?.Albums || []);
  const [showCreateAlbum, setShowCreateAlbum] = useState(false);
  const [isPrivateARoom, setIsPrivateARoom] = useState(false);
  const [uploadAlbum, setUploadAlbum] = useState(null);
  const [albumMedia, setAlbumMedia] = useState({});
  const [loadingMedia, setLoadingMedia] = useState({});
  const [lightbox, setLightbox] = useState(null); // { albumId, index }
  const [moderationTarget, setModerationTarget] = useState(null); // { item, albumId, action }
  const [moderationReason, setModerationReason] = useState("");
  const [moderating, setModerating] = useState(false);
  const [collapsedAlbums, setCollapsedAlbums] = useState(() => {
    // Collapse all albums except the first (or except target album if deep-linked)
    const initial = {};
    (initialGallery?.Albums || []).forEach((a, idx) => {
      if (targetAlbumId) {
        initial[a.Id] = Number(a.Id) !== Number(targetAlbumId);
      } else if (idx > 0) {
        initial[a.Id] = true;
      }
    });
    return initial;
  }); // albums manually collapsed
  const [deletingIds, setDeletingIds] = useState({}); // { [mediaId]: true } — loading state
  const [downloadingIds, setDownloadingIds] = useState({}); // { [mediaId]: true } — download in progress
  const [selectedIds, setSelectedIds] = useState({}); // { [mediaId]: albumId } — multi-select
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [visibleCount, setVisibleCount] = useState({}); // { [albumId]: number }
  const [savingGallery, setSavingGallery] = useState(false);
  const [savingAlbumId, setSavingAlbumId] = useState(null);
  const [deletingAlbumId, setDeletingAlbumId] = useState(null);
  const [deletingGallery, setDeletingGallery] = useState(false);
  const [showEditGalleryModal, setShowEditGalleryModal] = useState(false);
  const [editGalleryName, setEditGalleryName] = useState("");
  const [editAlbumTarget, setEditAlbumTarget] = useState(null);
  const [viewerApprovalsAlbum, setViewerApprovalsAlbum] = useState(null);
  const [pendingApprovalCounts, setPendingApprovalCounts] = useState({});
  const [editAlbumName, setEditAlbumName] = useState("");
  const [deleteAlbumTarget, setDeleteAlbumTarget] = useState(null);
  const [showDeleteGalleryModal, setShowDeleteGalleryModal] = useState(false);
  const [reorderingAlbums, setReorderingAlbums] = useState(false);
  // albumThumbnails: { [albumId]: mediaId[] } — ordered array, index+1 = displayOrder, max 3
  const [albumThumbnails, setAlbumThumbnails] = useState({});
  const [togglingThumbnail, setTogglingThumbnail] = useState({}); // { [mediaId]: true }
  const [homeImages, setHomeImages] = useState({});
  const [togglingHomeImage, setTogglingHomeImage] = useState({}); // { [mediaId]: true }
  const [togglingMainScreenId, setTogglingMainScreenId] = useState(null);
  const INITIAL_SHOW = 20;
  const LOAD_MORE_STEP = 20;
  const mainScreenAlbumIds = getMainScreenAlbumIds(albums);

  const albumSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleAlbumDragEnd = async (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;

    const oldIndex = albums.findIndex((a) => a.Id === active.id);
    const newIndex = albums.findIndex((a) => a.Id === over.id);
    const reordered = arrayMove(albums, oldIndex, newIndex);
    setAlbums(reordered);

    const orderData = reordered.map((a, i) => ({ id: a.Id, order: i + 1 }));
    setReorderingAlbums(true);
    try {
      await updateAlbumOrder(orderData);
    } catch {
      toast.error(t("galleryDetail.toasts.failedSaveAlbumOrder"));
      refetchGallery();
    } finally {
      setReorderingAlbums(false);
    }
  };

  // chatRoomId: for room flow use the room's Id, for event flow use the eventId param
  // Also fallback to gallery.ChatRoomId / roomId in case navigation state is lost (e.g. page refresh)
  const chatRoomId = state?.room?.Id || state?.chatRoomId || eventId || gallery?.ChatRoomId || roomId;
  useEffect(() => {
    if (!roomId || !chatRoomId) return;
    getRoomAccess(chatRoomId)
      .then((res) => setIsPrivateARoom(Number(res.data?.AccessMode) !== 1))
      .catch(() => setIsPrivateARoom(false));
  }, [roomId, chatRoomId]);
  const { uploadVersion } = useUpload();

  const refreshPendingApprovalCounts = useCallback(async () => {
    if (!roomId) return;
    const restrictedAlbums = albums.filter((album) => album.ContainsMinors || Number(album.ARoomVisibility) === 3);
    const results = await Promise.all(restrictedAlbums.map(async (album) => {
      try {
        const res = await getARoomAlbumViewerRequests(album.Id);
        const requests = res.data?.Result || res.data || [];
        return [album.Id, requests.length];
      } catch { return [album.Id, 0]; }
    }));
    setPendingApprovalCounts(Object.fromEntries(results));
  }, [albums, roomId]);

  useEffect(() => { refreshPendingApprovalCounts(); }, [refreshPendingApprovalCounts]);

  const refetchGallery = useCallback(() => {
    if (!chatRoomId) return;
    getGalleriesByEvent(chatRoomId)
      .then((res) => {
        const targetGalleryId = Number(gallery?.Id || galleryId || 0);
        const updated = (res.data?.Result || []).find((g) => Number(g.Id) === targetGalleryId);
        if (updated) {
          setGallery(updated);
          setAlbums(updated.Albums || []);
          // Only re-fetch media for albums that are already expanded (not collapsed)
          // Bust the album media cache first so we don't get stale data after an upload
          (updated.Albums || []).forEach((a) => {
            if (!collapsedAlbums[a.Id]) {
              invalidateAlbumCache(chatRoomId, a.Id);
              fetchAlbumMedia(a.Id);
            }
          });
        }
      })
      .catch(() => {});
  }, [chatRoomId, gallery?.Id, galleryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Support direct URL open (without navigation state) by resolving gallery from params.
  useEffect(() => {
    if (gallery || !chatRoomId || !galleryId) {
      if (gallery) setIsResolvingGallery(false);
      return;
    }

    let cancelled = false;
    setIsResolvingGallery(true);

    getGalleriesByEvent(chatRoomId)
      .then((res) => {
        if (cancelled) return;
        const found = (res.data?.Result || []).find((g) => Number(g.Id) === Number(galleryId));
        if (found) {
          setGallery(found);
          setAlbums(found.Albums || []);
          setCollapsedAlbums(() => {
            const initial = {};
            (found.Albums || []).forEach((a, idx) => {
              if (targetAlbumId) {
                initial[a.Id] = Number(a.Id) !== Number(targetAlbumId);
              } else if (idx > 0) {
                initial[a.Id] = true;
              }
            });
            return initial;
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsResolvingGallery(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gallery, chatRoomId, galleryId, targetAlbumId]);

  const fetchAlbumMedia = useCallback((albumId) => {
    setLoadingMedia((prev) => ({ ...prev, [albumId]: true }));
    getAlbumMedia(chatRoomId, albumId)
      .then((res) => {
        const items = res.data?.Result || [];
        setAlbumMedia((prev) => ({ ...prev, [albumId]: items }));
        // Seed thumbnail state — ordered array sorted by DisplayOrder
        const thumbIds = items
          .filter((m) => m.IsAlbumThumbnail)
          .sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0))
          .map((m) => m.Id);
        setAlbumThumbnails((prev) => ({ ...prev, [albumId]: thumbIds }));
        const homeIds = items
          .filter((m) => m.IsHomeImage)
          .sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0))
          .map((m) => m.Id);
        setHomeImages((prev) => ({ ...prev, [albumId]: homeIds }));
      })
      .catch(() => {})
      .finally(() => {
        setLoadingMedia((prev) => ({ ...prev, [albumId]: false }));
      });
  }, [chatRoomId]);

  // Auto-load only the first album's media on mount; others are collapsed and load on expand
  useEffect(() => {
    if (!albums.length || !chatRoomId) return;
    const firstAlbumId = albums[0]?.Id;
    if (!firstAlbumId) return;
    if (!albumMedia[firstAlbumId] && !loadingMedia[firstAlbumId]) {
      fetchAlbumMedia(firstAlbumId);
    }
  }, [albums, chatRoomId, albumMedia, loadingMedia, fetchAlbumMedia]);

  // Refetch gallery + album media when any upload session completes
  useEffect(() => {
    if (uploadVersion === 0 || !chatRoomId) return;
    refetchGallery();
  }, [uploadVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset one-shot deep-link focus guard when route/target changes.
  useEffect(() => {
    didFocusTargetAlbumRef.current = false;
  }, [targetAlbumId, eventId, roomId]);

  // If opened via /...?albumId=123, expand and scroll to that album.
  useEffect(() => {
    if (!targetAlbumId || !albums.length || didFocusTargetAlbumRef.current) return;

    const target = albums.find((a) => Number(a.Id) === Number(targetAlbumId));
    if (!target) return;

    setCollapsedAlbums((prev) => ({ ...prev, [target.Id]: false }));
    if (!albumMedia[target.Id] && !loadingMedia[target.Id]) {
      fetchAlbumMedia(target.Id);
    }

    const scrollToTarget = () => {
      const el = document.getElementById(`album-${target.Id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        didFocusTargetAlbumRef.current = true;
      }
    };

    // Delay to ensure album cards are rendered before scroll.
    setTimeout(scrollToTarget, 120);
  }, [targetAlbumId, albums, albumMedia, loadingMedia, fetchAlbumMedia]);

  const toggleAlbum = (albumId) => {
    setCollapsedAlbums((prev) => {
      const willExpand = prev[albumId]; // was collapsed, now expanding
      if (willExpand && !albumMedia[albumId] && !loadingMedia[albumId]) {
        fetchAlbumMedia(albumId);
      }
      return { ...prev, [albumId]: !prev[albumId] };
    });
  };

  const openEditGalleryModal = () => {
    setEditGalleryName(gallery?.Name || "");
    setShowEditGalleryModal(true);
  };

  const handleEditGalleryName = async () => {
    const trimmed = editGalleryName.trim();
    if (!trimmed) {
      toast.warning(t("galleryDetail.toasts.galleryNameRequired"));
      return;
    }

    setSavingGallery(true);
    try {
      await updateEventGallery(gallery.Id, {
        EventId: Number(gallery.EventId || eventId || chatRoomId),
        Name: trimmed,
      });
      toast.success(t("galleryDetail.toasts.galleryUpdated"));
      setShowEditGalleryModal(false);
      refetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("galleryDetail.toasts.failedUpdateGallery"));
    } finally {
      setSavingGallery(false);
    }
  };

  const handleDeleteGallery = async () => {
    if (deletingGallery) return;

    setDeletingGallery(true);
    try {
      await archiveEventGallery({
        galleryId: gallery.Id,
        eventId: Number(gallery.EventId || eventId || chatRoomId),
        name: gallery.Name,
      });
      toast.success(t("galleryDetail.toasts.galleryDeleted"));
      navigate(backPath, { state: backState });
    } catch (err) {
      toast.error(err.response?.data?.Message || t("galleryDetail.toasts.failedDeleteGallery"));
    } finally {
      setDeletingGallery(false);
      setShowDeleteGalleryModal(false);
    }
  };

  const openEditAlbumModal = (album) => {
    setEditAlbumTarget(album);
    setEditAlbumName(album?.Name || "");
  };

  const handleEditAlbumName = async () => {
    if (!editAlbumTarget) return;
    const trimmed = editAlbumName.trim();
    if (!trimmed) {
      toast.warning(t("galleryDetail.toasts.albumNameRequired"));
      return;
    }

    setSavingAlbumId(editAlbumTarget.Id);
    try {
      await updateGalleryAlbum({
        AlbumId: Number(editAlbumTarget.Id),
        Name: trimmed,
      });
      toast.success(t("galleryDetail.toasts.albumUpdated"));
      setEditAlbumTarget(null);
      refetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("galleryDetail.toasts.failedUpdateAlbum"));
    } finally {
      setSavingAlbumId(null);
    }
  };

  const openDeleteAlbumModal = (album) => {
    setDeleteAlbumTarget(album);
  };

  const handleDeleteAlbum = async () => {
    if (!deleteAlbumTarget || deletingAlbumId === deleteAlbumTarget.Id) return;

    setDeletingAlbumId(deleteAlbumTarget.Id);
    try {
      await archiveGalleryAlbum({
        albumId: deleteAlbumTarget.Id,
        name: deleteAlbumTarget.Name,
      });
      toast.success(t("galleryDetail.toasts.albumDeleted"));
      setDeleteAlbumTarget(null);
      refetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("galleryDetail.toasts.failedDeleteAlbum"));
    } finally {
      setDeletingAlbumId(null);
    }
  };

  const showMore = (albumId) => {
    setVisibleCount((prev) => ({
      ...prev,
      [albumId]: (prev[albumId] || INITIAL_SHOW) + LOAD_MORE_STEP,
    }));
  };

  const handleDelete = async (albumId, mediaId) => {
    if (deletingIds[mediaId]) return;
    setDeletingIds((prev) => ({ ...prev, [mediaId]: true }));
    try {
      await archiveMedia([mediaId]);
      invalidateAlbumCache(chatRoomId, albumId);
      setAlbumMedia((prev) => ({
        ...prev,
        [albumId]: (prev[albumId] || []).filter((m) => m.Id !== mediaId),
      }));
      toast.success(t("galleryDetail.toasts.mediaDeleted"));
    } catch {
      toast.error(t("galleryDetail.toasts.failedDeleteMedia"));
    } finally {
      setDeletingIds((prev) => ({ ...prev, [mediaId]: false }));
    }
  };

  const openModeration = (action) => {
    setModerationTarget({ item: lightboxItem, albumId: lightbox.albumId, action });
    setModerationReason("");
    closeLightbox();
  };

  const handleModeration = async () => {
    if (!moderationTarget || !moderationReason.trim() || moderating) return;
    setModerating(true);
    try {
      await moderateSubPromoterMedia(
        moderationTarget.item.Id,
        moderationTarget.action,
        moderationReason.trim()
      );
      invalidateAlbumCache(chatRoomId, moderationTarget.albumId);
      setAlbumMedia((prev) => ({
        ...prev,
        [moderationTarget.albumId]: (prev[moderationTarget.albumId] || []).filter(
          (item) => item.Id !== moderationTarget.item.Id
        ),
      }));
      toast.success(t("galleryDetail.moderation.success"));
      setModerationTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("galleryDetail.moderation.error"));
    } finally {
      setModerating(false);
    }
  };

  const selectedCount = Object.keys(selectedIds).length;

  const handleToggleThumbnail = async (albumId, mediaId) => {
    const current = albumThumbnails[albumId] || [];
    const isCurrentlyThumb = current.includes(mediaId);

    // Enforce max 3
    if (!isCurrentlyThumb && current.length >= 3) {
      toast.warning(t("galleryDetail.toasts.maxThumbnails"));
      return;
    }

    const displayOrder = isCurrentlyThumb ? null : current.length + 1;

    setTogglingThumbnail((prev) => ({ ...prev, [mediaId]: true }));
    try {
      await setAlbumThumbnail(mediaId, !isCurrentlyThumb, displayOrder);
      setAlbumThumbnails((prev) => {
        const arr = prev[albumId] || [];
        const next = isCurrentlyThumb
          ? arr.filter((id) => id !== mediaId)
          : [...arr, mediaId];
        return { ...prev, [albumId]: next };
      });
    } catch {
      toast.error(t("galleryDetail.toasts.failedUpdateThumbnail"));
    } finally {
      setTogglingThumbnail((prev) => ({ ...prev, [mediaId]: false }));
    }
  };

  const handleToggleHomeImage = async (albumId, mediaId) => {
    const current = homeImages[albumId] || [];
    const isCurrentlyHomeImage = current.includes(mediaId);

    // Keep the same behavior as album thumbnails.
    if (!isCurrentlyHomeImage && current.length >= 3) {
      toast.warning(t("galleryDetail.toasts.maxHomeImages"));
      return;
    }

    const displayOrder = isCurrentlyHomeImage ? null : current.length + 1;

    setTogglingHomeImage((prev) => ({ ...prev, [mediaId]: true }));
    try {
      await setHomeImage(mediaId, !isCurrentlyHomeImage, displayOrder);
      setHomeImages((prev) => {
        const arr = prev[albumId] || [];
        const next = isCurrentlyHomeImage
          ? arr.filter((id) => id !== mediaId)
          : [...arr, mediaId];
        return { ...prev, [albumId]: next };
      });
    } catch {
      toast.error(t("galleryDetail.toasts.failedUpdateHomeImage"));
    } finally {
      setTogglingHomeImage((prev) => ({ ...prev, [mediaId]: false }));
    }
  };

  const handleToggleMainScreen = async (album) => {
    const currentIds = getMainScreenAlbumIds(albums);
    const isCurrentlyMainScreen = currentIds.includes(album.Id);

    if (!isCurrentlyMainScreen && currentIds.length >= MAX_MAIN_SCREEN_ALBUMS) {
      toast.warning(t("galleryDetail.toasts.maxMainScreenAlbums", { max: MAX_MAIN_SCREEN_ALBUMS }));
      return;
    }

    const nextIds = isCurrentlyMainScreen
      ? currentIds.filter((id) => id !== album.Id)
      : [...currentIds, album.Id];

    const previousAlbums = albums;
    const nextAlbums = albums.map((item) => {
      const nextIndex = nextIds.indexOf(item.Id);
      if (nextIndex === -1) {
        if (!item.IsMainScreen && !item.MainScreenOrder) return item;
        return {
          ...item,
          IsMainScreen: false,
          MainScreenOrder: null,
        };
      }

      return {
        ...item,
        IsMainScreen: true,
        MainScreenOrder: nextIndex + 1,
      };
    });

    setTogglingMainScreenId(album.Id);
    setAlbums(nextAlbums);

    try {
      const removedIds = currentIds.filter((id) => !nextIds.includes(id));

      for (const removedId of removedIds) {
        await updateGalleryAlbum({
          AlbumId: Number(removedId),
          IsMainScreen: false,
        });
      }

      for (const [index, selectedId] of nextIds.entries()) {
        await updateGalleryAlbum({
          AlbumId: Number(selectedId),
          IsMainScreen: true,
          MainScreenOrder: index + 1,
        });
      }

      toast.success(isCurrentlyMainScreen ? t("galleryDetail.toasts.mainScreenRemoved") : t("galleryDetail.toasts.mainScreenAdded"));
    } catch (err) {
      setAlbums(previousAlbums);
      toast.error(err.response?.data?.Message || t("galleryDetail.toasts.failedUpdateMainScreen"));
    } finally {
      setTogglingMainScreenId(null);
    }
  };

  const toggleSelect = (mediaId, albumId) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      if (next[mediaId]) delete next[mediaId];
      else next[mediaId] = albumId;
      return next;
    });
  };

  const selectAllInAlbum = (albumId) => {
    const items = albumMedia[albumId] || [];
    const allSelected = items.length > 0 && items.every((m) => selectedIds[m.Id]);
    // Selection is scoped to one album at a time: selecting/deselecting in
    // this album must not keep a previous album's selection around (that
    // combined their counts — see the reported bug), so only this album's
    // entries survive here.
    const next = {};
    if (!allSelected) {
      items.forEach((m) => { next[m.Id] = albumId; });
    }
    setSelectedIds(next);
  };

  const clearSelection = () => setSelectedIds({});

  const handleBulkDelete = async () => {
    const ids = Object.keys(selectedIds).map(Number);
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      await archiveMedia(ids);
      const uniqueAlbumIds = [...new Set(Object.values(selectedIds))];
      uniqueAlbumIds.forEach((aId) => invalidateAlbumCache(chatRoomId, aId));
      const idsSet = new Set(ids);
      setAlbumMedia((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = next[key].filter((m) => !idsSet.has(m.Id));
        }
        return next;
      });
      toast.success(t("galleryDetail.toasts.mediaDeletedCount", { count: ids.length }));
      setSelectedIds({});
    } catch {
      toast.error(t("galleryDetail.toasts.failedDeleteSelected"));
    } finally {
      setBulkDeleting(false);
    }
  };

  const openLightbox = (albumId, index) => setLightbox({ albumId, index });
  const closeLightbox = () => setLightbox(null);

  const lightboxMedia = lightbox ? albumMedia[lightbox.albumId] || [] : [];
  const lightboxItem = lightbox ? lightboxMedia[lightbox.index] : null;

  const goNext = useCallback(() => {
    if (!lightbox) return;
    setLightbox((prev) => ({
      ...prev,
      index: (prev.index + 1) % lightboxMedia.length,
    }));
  }, [lightbox, lightboxMedia.length]);

  const goPrev = useCallback(() => {
    if (!lightbox) return;
    setLightbox((prev) => ({
      ...prev,
      index: (prev.index - 1 + lightboxMedia.length) % lightboxMedia.length,
    }));
  }, [lightbox, lightboxMedia.length]);

  const getDownloadUrl = (item) => {
    if (!item) return null;
    if (item.VideoURL) return item.VideoURL;
    return item.Url || null;
  };

  const getDownloadName = (item) => {
    const source = getDownloadUrl(item) || "";
    const sourceName = source.split("?")[0].split("/").pop();
    if (sourceName) return sourceName;
    const ext = item?.VideoURL ? "mp4" : "jpg";
    return `media-${item?.Id || "file"}.${ext}`;
  };

  const downloadMedia = async (item) => {
    const itemId = item?.Id;
    if (!itemId || downloadingIds[itemId]) return;

    const url = getDownloadUrl(item);
    if (!url) {
      toast.error(t("galleryDetail.toasts.downloadUrlMissing"));
      return;
    }

    setDownloadingIds((prev) => ({ ...prev, [itemId]: true }));
    try {
      await downloadMediaFile({
        url,
        fileName: getDownloadName(item),
      });
    } catch {
      toast.error(t("galleryDetail.toasts.downloadFailed"));
    } finally {
      setDownloadingIds((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleBulkDownload = async () => {
    const ids = Object.keys(selectedIds);
    if (!ids.length) return;

    setBulkDownloading(true);
    let started = 0;
    let failed = 0;

    try {
      for (const id of ids) {
        const albumId = selectedIds[id];
        const item = (albumMedia[albumId] || []).find((m) => String(m.Id) === String(id));
        if (!item) {
          failed += 1;
          continue;
        }
        try {
          const url = getDownloadUrl(item);
          if (!url) throw new Error("missing url");

          setDownloadingIds((prev) => ({ ...prev, [item.Id]: true }));
          await downloadMediaFile({
            url,
            fileName: getDownloadName(item),
          });
          started += 1;
          // Space out downloads so the browser doesn't cancel concurrent ones
          await new Promise((r) => setTimeout(r, 600));
        } catch {
          failed += 1;
        } finally {
          setDownloadingIds((prev) => ({ ...prev, [item.Id]: false }));
        }
      }

      if (started > 0) {
        toast.success(t("galleryDetail.toasts.downloadsStarted", { count: started }));
      }
      if (failed > 0) {
        toast.error(t("galleryDetail.toasts.downloadsFailed", { count: failed }));
      }
      // Mirror handleBulkDelete: clear the selection once the operation is
      // done so it doesn't carry over (combined with another album's picks)
      // into whatever the promoter selects next.
      setSelectedIds({});
    } finally {
      setBulkDownloading(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!lightbox) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightbox, goNext, goPrev]);

  if (!gallery) {
    return (
      <div>
        <Button variant="secondary" onClick={() => navigate(backPath, { state: backState })}>
          {t("galleryDetail.backToGalleries")}
        </Button>
        <p style={{ color: "#888", marginTop: 20 }}>
          {isResolvingGallery ? t("galleryDetail.loadingGallery") : t("galleryDetail.galleryNotFound")}
        </p>
      </div>
    );
  }

  return (
    <div>
      <Button
        variant="secondary"
        onClick={() => navigate(backPath, { state: backState })}
        style={{ marginBottom: 20 }}
      >
        {t("galleryDetail.backToGalleries")}
      </Button>

      {/* Gallery Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.titleRow}>
            <h2 style={styles.title}>{gallery.Name}</h2>
            {!isRestrictedGallery(gallery) && (
              <button
                style={styles.headerActionBtn}
                onClick={openEditGalleryModal}
                disabled={savingGallery || deletingGallery}
              >
                {savingGallery ? t("galleryDetail.album.saving") : <Pencil size={12} strokeWidth={2.25} />}
              </button>
            )}

            {!isRestrictedGallery(gallery) && (
              <button
                style={{ ...styles.headerActionBtn, ...styles.headerDeleteBtn }}
                onClick={() => setShowDeleteGalleryModal(true)}
                disabled={savingGallery || deletingGallery}
              >
                {deletingGallery ? t("galleryDetail.album.deleting") : <Trash2 size={12} strokeWidth={2.25} />}
              </button>
            )}
          </div>
          <div style={styles.metaRow}>
            {gallery.IsPremium && <span style={styles.premiumBadge}><Star size={9} strokeWidth={2.5} /> {t("galleryDetail.album.premium")}</span>}
            {gallery.IsWatermark && <span style={styles.tag}>{t("galleryDetail.tags.watermark")}</span>}
            {gallery.IsMediaHandlesGallery && <span style={styles.tag}>{t("galleryDetail.tags.mediaHandles")}</span>}
            {gallery.IsUserGallery && <span style={styles.tag}>{t("galleryDetail.tags.userGallery")}</span>}
          </div>
          <div style={styles.counts}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><ImageIcon size={13} strokeWidth={2} /> <strong>{Object.values(albumMedia).reduce((s, arr) => s + arr.length, 0) || gallery.MediaCount}</strong> {t("galleryDetail.mediaUnit")}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><FolderOpen size={13} strokeWidth={2} /> <strong>{albums.length || gallery.AlbumCount}</strong> {t("galleryDetail.albumsUnit")}</span>
          </div>
          {gallery.Package && (
            <div style={styles.packageBox}>
              <strong>{gallery.Package.Name}</strong>
              <span style={styles.packageDetail}> — {gallery.Package.Description}</span>
              <span style={styles.packagePrice}> {gallery.Package.DefaultPrice} {gallery.Package.Currency}</span>
            </div>
          )}
        </div>
      </div>

      {/* Albums */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h3 style={styles.albumsHeading}>
          {t("galleryDetail.albumsHeading")} <span style={styles.albumCount}>({albums.length})</span>
        </h3>
        {reorderingAlbums && (
          <span style={styles.orderSavingLabel}>{t("galleryDetail.savingOrder")}</span>
        )}
        {!isRestrictedGallery(gallery) && (
          <Button onClick={() => setShowCreateAlbum(true)} style={{ padding: "6px 14px", fontSize: 13 }}>
            {t("galleryDetail.newAlbum")}
          </Button>
        )}
      </div>

      {albums.length === 0 ? (
        <p style={{ color: "#888", fontSize: 14 }}>{t("galleryDetail.noAlbums")}</p>
      ) : (
        <DndContext sensors={albumSensors} collisionDetection={closestCenter} onDragEnd={handleAlbumDragEnd}>
          <SortableContext items={albums.map((a) => a.Id)} strategy={verticalListSortingStrategy}>
            <div style={styles.albumsList}>
              {albums.map((album) => (
                <SortableAlbumCard
                  key={album.Id}
                  album={album}
                  targetAlbumId={targetAlbumId}
                  collapsedAlbums={collapsedAlbums}
                  albumMedia={albumMedia}
                  loadingMedia={loadingMedia}
                  visibleCount={visibleCount}
                  selectedIds={selectedIds}
                  deletingIds={deletingIds}
                  downloadingIds={downloadingIds}
                  mainScreenAlbumIds={mainScreenAlbumIds}
                  togglingMainScreenId={togglingMainScreenId}
                  savingAlbumId={savingAlbumId}
                  deletingAlbumId={deletingAlbumId}
                  selectedCount={selectedCount}
                  INITIAL_SHOW={INITIAL_SHOW}
                  albumThumbnails={albumThumbnails}
                  togglingThumbnail={togglingThumbnail}
                  homeImages={homeImages}
                  togglingHomeImage={togglingHomeImage}
                  onToggle={toggleAlbum}
                  onEdit={openEditAlbumModal}
                  onDelete={openDeleteAlbumModal}
                  onUpload={(a) => setUploadAlbum(a)}
                  onSelectAll={selectAllInAlbum}
                  onToggleSelect={toggleSelect}
                  onOpenLightbox={openLightbox}
                  onDownload={downloadMedia}
                  onDeleteMedia={handleDelete}
                  onShowMore={showMore}
                  onToggleThumbnail={handleToggleThumbnail}
                  onToggleHomeImage={handleToggleHomeImage}
                  onToggleMainScreen={handleToggleMainScreen}
                  isARoom={Boolean(roomId)}
                  pendingRequestCount={pendingApprovalCounts[album.Id] || 0}
                  onManageViewerApprovals={(target) => setViewerApprovalsAlbum(target)}
                  restrictedToDownload={isRestrictedGallery(gallery)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {moderationTarget && (
        <Modal
          title={t(`galleryDetail.moderation.${moderationTarget.action.toLowerCase()}Title`)}
          subtitle={t("galleryDetail.moderation.quarantineNotice")}
          onClose={() => !moderating && setModerationTarget(null)}
          busy={moderating}
          maxWidth={480}
          footer={(
            <>
              <Button variant="secondary" onClick={() => setModerationTarget(null)} disabled={moderating}>
                {t("common:actions.cancel")}
              </Button>
              <Button variant="danger" onClick={handleModeration} disabled={moderating || !moderationReason.trim()}>
                {moderating ? t("galleryDetail.moderation.saving") : t("galleryDetail.moderation.confirm")}
              </Button>
            </>
          )}
        >
          <label htmlFor="media-moderation-reason" style={styles.moderationLabel}>
            {t("galleryDetail.moderation.reasonLabel")}
          </label>
          <textarea
            id="media-moderation-reason"
            value={moderationReason}
            maxLength={1000}
            rows={5}
            autoFocus
            onChange={(event) => setModerationReason(event.target.value)}
            placeholder={t("galleryDetail.moderation.reasonPlaceholder")}
            style={styles.moderationTextarea}
          />
          <div style={styles.moderationCount}>{moderationReason.length}/1000</div>
        </Modal>
      )}

      {showCreateAlbum && (
        <CreateAlbumModal
          galleryId={gallery.Id}
          galleryName={gallery.Name}
          isARoom={Boolean(roomId)}
          isPrivateARoom={isPrivateARoom}
          onClose={() => setShowCreateAlbum(false)}
          onCreated={() => {
            getGalleriesByEvent(chatRoomId)
              .then((res) => {
                const updated = (res.data?.Result || []).find((g) => g.Id === gallery.Id);
                if (updated?.Albums) {
                  setAlbums(updated.Albums);
                  updated.Albums.forEach((a) => {
                    if (!albumMedia[a.Id]) fetchAlbumMedia(a.Id);
                  });
                }
              })
              .catch(() => {});
          }}
        />
      )}


      {uploadAlbum && (
        <UploadToAlbumModal
          album={uploadAlbum}
          gallery={gallery}
          chatRoomId={chatRoomId}
          onClose={() => setUploadAlbum(null)}
          onUploaded={() => {
            refetchGallery();
            setUploadAlbum(null);
          }}
        />
      )}

      {/* Bulk Delete Bar */}
      {selectedCount > 0 && (
        <div style={styles.bulkBar}>
          <span style={{ fontWeight: 600 }}>{t("galleryDetail.bulkBar.selected", { count: selectedCount })}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={styles.bulkCancelBtn} onClick={clearSelection}>{t("common:actions.cancel")}</button>
            <button
              style={styles.bulkDownloadBtn}
              onClick={handleBulkDownload}
              disabled={bulkDeleting || bulkDownloading}
            >
              {bulkDownloading ? t("galleryDetail.bulkBar.downloading") : <><Download size={13} strokeWidth={2.25} /> {t("galleryDetail.bulkBar.download", { count: selectedCount })}</>}
            </button>
            {!isRestrictedGallery(gallery) && (
              <button
                style={styles.bulkDeleteBtn}
                onClick={handleBulkDelete}
                disabled={bulkDeleting || bulkDownloading}
              >
                {bulkDeleting ? t("galleryDetail.bulkBar.deleting") : <><Trash2 size={13} strokeWidth={2.25} /> {t("galleryDetail.bulkBar.delete", { count: selectedCount })}</>}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightbox && lightboxItem && (
        <div style={styles.lbOverlay} onClick={closeLightbox}>
          <div style={styles.lbContent} onClick={(e) => e.stopPropagation()}>
            {/* Close */}
            <button style={styles.lbClose} onClick={closeLightbox}><X size={22} strokeWidth={2} /></button>

            {/* Download */}
            <button
              style={{
                ...styles.lbDownload,
                opacity: lightboxItem && downloadingIds[lightboxItem.Id] ? 0.6 : 1,
                cursor: lightboxItem && downloadingIds[lightboxItem.Id] ? "not-allowed" : "pointer",
              }}
              onClick={() => downloadMedia(lightboxItem)}
              title={t("galleryDetail.lightbox.download")}
              disabled={!!(lightboxItem && downloadingIds[lightboxItem.Id])}
            >
              {lightboxItem && downloadingIds[lightboxItem.Id] ? t("galleryDetail.lightbox.downloading") : t("galleryDetail.lightbox.downloadLabel")}
            </button>

            {canModerateSingleMedia && String(lightboxItem.UserId) !== String(effectiveOwnerId) && (
              <div style={styles.lbModerationActions}>
                <button style={styles.lbModerationBtn} onClick={() => openModeration("Block")}>
                  <Ban size={14} /> {t("galleryDetail.moderation.block")}
                </button>
                <button style={{ ...styles.lbModerationBtn, ...styles.lbRemoveBtn }} onClick={() => openModeration("Remove")}>
                  <Trash2 size={14} /> {t("galleryDetail.moderation.remove")}
                </button>
              </div>
            )}

            {/* Counter */}
            <div style={styles.lbCounter}>
              {lightbox.index + 1} / {lightboxMedia.length}
            </div>

            {/* Prev */}
            {lightboxMedia.length > 1 && (
              <button style={styles.lbArrowLeft} onClick={goPrev}>‹</button>
            )}

            {/* Media */}
            <div style={styles.lbMedia}>
              {lightboxItem.VideoURL ? (
                <VideoPlayer
                  key={lightboxItem.Id}
                  src={lightboxItem.Url}
                  fallbackSrc={lightboxItem.CompressedUrl || lightboxItem.VideoURL}
                  poster={lightboxItem.VideoThumbnail}
                  style={styles.lbImage}
                  autoPlay
                />
              ) : (
                <img
                  key={lightboxItem.Id}
                  src={lightboxItem.CompressedUrl || lightboxItem.Url}
                  alt={lightboxItem.Name || t("galleryDetail.album.imageAlt", { id: lightboxItem.Id })}
                  style={styles.lbImage}
                  onError={(e) => { e.target.src = lightboxItem.Url; }}
                />
              )}
            </div>

            {/* Next */}
            {lightboxMedia.length > 1 && (
              <button style={styles.lbArrowRight} onClick={goNext}>›</button>
            )}
          </div>
        </div>
      )}

      {showEditGalleryModal && (
        <EditNameModal
          title={t("galleryDetail.editGalleryModal.title")}
          label={t("galleryDetail.editGalleryModal.label")}
          value={editGalleryName}
          onChange={setEditGalleryName}
          onSave={handleEditGalleryName}
          onCancel={() => !savingGallery && setShowEditGalleryModal(false)}
          loading={savingGallery}
          saveText={t("common:actions.save")}
        />
      )}

      {showDeleteGalleryModal && (
        <ConfirmModal
          title={t("galleryDetail.deleteGalleryModal.title")}
          message={t("galleryDetail.deleteGalleryModal.message", { name: gallery?.Name || "" })}
          confirmText={t("common:actions.delete")}
          danger
          loading={deletingGallery}
          onConfirm={handleDeleteGallery}
          onCancel={() => !deletingGallery && setShowDeleteGalleryModal(false)}
        />
      )}

      {editAlbumTarget && (
        Boolean(roomId) ? <ARoomAlbumPolicyModal
          album={editAlbumTarget}
          isPrivateARoom={isPrivateARoom}
          onClose={() => !savingAlbumId && setEditAlbumTarget(null)}
          onSaved={async (payload) => {
            try {
              await updateGalleryAlbum(payload);
              toast.success(t("galleryDetail.toasts.albumUpdated"));
              setEditAlbumTarget(null);
              refetchGallery();
            } catch (err) {
              toast.error(err.response?.data?.Message || t("galleryDetail.toasts.failedUpdateAlbum"));
              throw err;
            }
          }}
        /> : <EditNameModal
          title={t("galleryDetail.editAlbumModal.title")}
          label={t("galleryDetail.editAlbumModal.label")}
          value={editAlbumName}
          onChange={setEditAlbumName}
          onSave={handleEditAlbumName}
          onCancel={() => !savingAlbumId && setEditAlbumTarget(null)}
          loading={savingAlbumId === editAlbumTarget.Id}
          saveText={t("common:actions.save")}
        />
      )}

      {viewerApprovalsAlbum && (
        <ARoomAlbumViewerApprovalsModal
          album={viewerApprovalsAlbum}
          onClose={() => setViewerApprovalsAlbum(null)}
          onChanged={refreshPendingApprovalCounts}
        />
      )}

      {deleteAlbumTarget && (
        <ConfirmModal
          title={t("galleryDetail.deleteAlbumModal.title")}
          message={t("galleryDetail.deleteAlbumModal.message", { name: deleteAlbumTarget.Name })}
          confirmText={t("common:actions.delete")}
          danger
          loading={deletingAlbumId === deleteAlbumTarget.Id}
          onConfirm={handleDeleteAlbum}
          onCancel={() => !deletingAlbumId && setDeleteAlbumTarget(null)}
        />
      )}
    </div>
  );
};

const styles = {
  header: {
    background: "#fff",
    borderRadius: 14,
    padding: "20px 24px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    marginBottom: 28,
    maxWidth: 680,
  },
  headerLeft: {},
  titleRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" },
  title: { margin: "0 0 10px", fontSize: 22, fontWeight: 700, color: "#1e1e2d" },
  headerActionBtn: {
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    padding: "5px 10px",
    cursor: "pointer",
  },
  headerDeleteBtn: {
    borderColor: "#fecaca",
    color: "#b91c1c",
    background: "#fff5f5",
  },
  metaRow: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 },
  counts: { display: "flex", gap: 20, fontSize: 14, color: "#555", marginBottom: 10 },
  premiumBadge: {
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 11, fontWeight: 700,
    background: "#fef3c7", color: "#d97706",
    padding: "2px 10px", borderRadius: 20,
  },
  tag: {
    fontSize: 11, background: "#f5f5f5", color: "#888",
    padding: "2px 10px", borderRadius: 20,
  },
  packageBox: {
    marginTop: 8,
    background: "#f9f8ff",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 13,
    color: "#444",
    borderLeft: "3px solid #6c63ff",
  },
  packageDetail: { color: "#666" },
  packagePrice: { fontWeight: 700, color: "#6c63ff" },

  albumsHeading: { margin: 0, fontSize: 18, fontWeight: 700, color: "#1e1e2d" },
  albumCount: { fontSize: 14, fontWeight: 400, color: "#888" },
  orderSavingLabel: { fontSize: 12, fontWeight: 600, color: "#6c63ff" },
  albumsList: { display: "flex", flexDirection: "column", gap: 12, maxWidth: 860 },
  dragHandle: {
    cursor: "grab",
    fontSize: 18,
    color: "#bbb",
    padding: "0 4px",
    userSelect: "none",
    lineHeight: 1,
  },

  albumCard: {
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  albumHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 20px",
    cursor: "pointer",
    userSelect: "none",
  },
  albumLeft: { flex: 1 },
  albumNameRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" },
  albumName: { fontWeight: 700, fontSize: 15, color: "#1e1e2d", display: "block", marginBottom: 6 },
  albumActionBtn: {
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    cursor: "pointer",
  },
  pendingApprovalDot: {
    position: "absolute", top: -4, right: -4, width: 8, height: 8,
    borderRadius: "50%", background: "#ef4444", border: "1.5px solid #fff",
  },
  mainScreenBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#475569",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 10px",
    cursor: "pointer",
    lineHeight: 1,
  },
  mainScreenBtnActive: {
    borderColor: "#2563eb",
    background: "#eff6ff",
    color: "#1d4ed8",
  },
  albumDeleteBtn: {
    borderColor: "#fecaca",
    color: "#b91c1c",
    background: "#fff5f5",
  },
  albumMeta: { display: "flex", flexWrap: "wrap", gap: 8, fontSize: 12, color: "#666" },
  mainScreenTag: {
    fontSize: 10,
    fontWeight: 700,
    background: "#eff6ff",
    color: "#1d4ed8",
    padding: "2px 8px",
    borderRadius: 20,
  },
  chevron: { fontSize: 12, color: "#888" },
  uploadBtn: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "#6c63ff",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    flexShrink: 0,
  },

  imagesGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    padding: "0 16px 16px",
  },
  targetAlbumCard: {
    border: "2px solid #6c63ff",
    boxShadow: "0 0 0 3px rgba(108,99,255,0.16)",
  },
  mediaCard: {
    width: 120,
    borderRadius: 8,
    overflow: "hidden",
    background: "#f0f0f0",
    position: "relative",
    cursor: "pointer",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  },
  image: {
    width: "100%",
    height: 120,
    objectFit: "cover",
    display: "block",
  },
  playOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.3)",
    pointerEvents: "none",
  },
  playBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.9)",
    color: "#1e1e2d",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    paddingLeft: 2,
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  },
  deleteBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 26,
    height: 26,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
    lineHeight: 1,
  },
  downloadBtn: {
    position: "absolute",
    bottom: 4,
    right: 4,
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 26,
    height: 26,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
    lineHeight: 1,
  },
  thumbBtn: {
    position: "absolute",
    bottom: 4,
    left: 4,
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 26,
    height: 26,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
    lineHeight: 1,
  },
  homeBtn: {
    position: "absolute",
    bottom: 4,
    left: 34,
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 26,
    height: 26,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
    lineHeight: 1,
  },
  // Bottom row is already thumbBtn (left:4) + homeBtn (left:34, spanning to
  // 60px) + downloadBtn (right:4) on a 120px-wide card — a bottom-centered
  // badge (x=60) sat right on top of homeBtn whenever a media item had
  // likes. Top-center is the only unused corner (select/delete/thumbnail
  // badges all live at the left/right edges), so the likes badge moved there.
  likesOverlay: {
    position: "absolute",
    top: 4,
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 20,
    padding: "2px 7px",
    zIndex: 3,
    pointerEvents: "none",
    whiteSpace: "nowrap",
  },
  albumThumbBadge: {
    position: "absolute",
    top: 4,
    right: 60,
    background: "rgba(108,99,255,0.85)",
    color: "#fff",
    fontSize: 12,
    borderRadius: "50%",
    width: 22,
    height: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3,
    pointerEvents: "none",
  },
  homeImageBadge: {
    position: "absolute",
    top: 4,
    right: 34,
    background: "rgba(34,197,94,0.92)",
    color: "#fff",
    fontSize: 12,
    borderRadius: "50%",
    width: 22,
    height: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3,
    pointerEvents: "none",
  },
  selectBtn: {
    position: "absolute",
    top: 4,
    left: 4,
    color: "#fff",
    border: "2px solid #fff",
    borderRadius: "50%",
    width: 22,
    height: 22,
    fontSize: 12,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
    lineHeight: 1,
    fontWeight: 700,
  },
  selectAllBtn: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "none",
    color: "#6c63ff",
    border: "1px solid #6c63ff",
    borderRadius: 8,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    flexShrink: 0,
  },
  bulkBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "#fff",
    borderTop: "1px solid #e5e7eb",
    boxShadow: "0 -2px 12px rgba(0,0,0,0.1)",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    // Above PSAAIAssistant's floating launcher (zIndex 1040, bottom-right
    // corner) - this bar's Delete/Download buttons were rendering underneath
    // it and were unclickable in that corner.
    zIndex: 1041,
    fontSize: 14,
  },
  bulkDeleteBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "#e53935",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  bulkDownloadBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  bulkCancelBtn: {
    background: "none",
    color: "#666",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 18px",
    fontSize: 13,
    cursor: "pointer",
  },
  showMoreBtn: {
    width: 120,
    height: 120,
    borderRadius: 8,
    border: "2px dashed #d1d5db",
    background: "#fafafa",
    color: "#6c63ff",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 10,
  },

  // Lightbox
  lbOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.92)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lbContent: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lbClose: {
    position: "absolute",
    top: 16,
    right: 24,
    background: "transparent",
    border: "none",
    color: "#fff",
    fontSize: 28,
    cursor: "pointer",
    zIndex: 1002,
    padding: "4px 10px",
    borderRadius: 8,
    lineHeight: 1,
  },
  lbDownload: {
    position: "absolute",
    top: 16,
    right: 78,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    zIndex: 1002,
    padding: "8px 12px",
    borderRadius: 8,
    lineHeight: 1,
  },
  lbModerationActions: { position: "absolute", top: 62, right: 24, zIndex: 1002, display: "flex", gap: 8 },
  lbModerationBtn: { display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8, padding: "8px 11px", color: "#fff", background: "rgba(180,83,9,0.86)", cursor: "pointer", fontWeight: 700 },
  lbRemoveBtn: { background: "rgba(211,61,66,0.9)" },
  moderationLabel: { display: "block", marginBottom: 7, fontSize: 13, fontWeight: 700 },
  moderationTextarea: { width: "100%", boxSizing: "border-box", resize: "vertical", border: "1px solid #d8dae3", borderRadius: 8, padding: 10, font: "inherit" },
  moderationCount: { marginTop: 5, textAlign: "right", color: "#9599ab", fontSize: 11 },
  lbCounter: {
    position: "absolute",
    top: 20,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#ccc",
    fontSize: 14,
    fontWeight: 500,
    zIndex: 1002,
  },
  lbArrowLeft: {
    position: "absolute",
    left: 16,
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255,255,255,0.1)",
    border: "none",
    color: "#fff",
    fontSize: 48,
    cursor: "pointer",
    zIndex: 1002,
    padding: "8px 16px",
    borderRadius: 12,
    lineHeight: 1,
  },
  lbArrowRight: {
    position: "absolute",
    right: 16,
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255,255,255,0.1)",
    border: "none",
    color: "#fff",
    fontSize: 48,
    cursor: "pointer",
    zIndex: 1002,
    padding: "8px 16px",
    borderRadius: 12,
    lineHeight: 1,
  },
  lbMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    maxWidth: "85vw",
    maxHeight: "85vh",
  },
  lbImage: {
    maxWidth: "85vw",
    maxHeight: "85vh",
    objectFit: "contain",
    borderRadius: 8,
    display: "block",
  },
};

export default GalleryDetail;
